import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { currentUserAtom } from '@/hooks/use-auth';
import { format } from 'date-fns';
import { ArrowUpRight, ListChecks, Radio, Send, Target } from 'lucide-react';
import { ChecksBadge, ProviderBadge, ReviewBadge, SentDateRange } from '@/components/shared/automation-reply-badges';
import { toGapStatus } from '@/adapters/gap.adapter';
import { StatusBadge } from '@/modules/gaps/components/gap-badges';
import { formatDisplayId, formatStatusLabel } from '@/modules/gaps/utils';
import { toast } from '@/components/ui/sonner';
import { usePermissions } from '@/hooks/use-permissions';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { ReplyChecks } from './automation-reply-checks';
import { blockedByLabel, blockReasonLabel, humanize, providerOf, sentBetween } from '@/lib/automation-replies';
import { CHANNEL_ICONS } from '@/lib/automation-guards';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CopyButton } from '@/modules/simulator/ui/copy-button';
import { runAutomationChecks, useAutomationReplies, useAutomationReply } from '@/hooks/use-automation-replies';
import { useBusinessUnits } from '@/hooks/use-business-units';
import {
	FilterField,
	FiltersPopover,
	SETTINGS_HEADER_BUTTON_CLASS,
	SettingsSelect,
	SettingsTableRows,
	StatusTag,
	type SettingsColumn
} from '@/modules/settings';
import type { AutomationChecksRunSummary, AutomationReply, AutomationReplyFilter, AutomationReplyGap } from '@/services/automation-reply.service';
import {
	ALL_INTENTS,
	automationLogColumns,
	CHECKS_OPTIONS,
	OUTCOME_OPTIONS,
	type AutomationChecksFilter,
	type AutomationLogFilters,
	type AutomationOutcomeFilter
} from './automation-log-filters';

/**
 * Logs › Automation: every turn Kim was set to answer on its own for the selected brand, newest
 * first: the replies it sent and the ones it held back. A row opens the full message and its
 * metadata. The Slack "Automation Reply Sent" alert links here as `?entry=<id>&account=<id>`.
 */

/** What a run of the checks over past replies did, in one line. */
const runSummaryText = (summary: AutomationChecksRunSummary): string => {
	if (summary.checked === 0) return 'None of these replies has an intent with checks. Set them on Intents › Active.';
	const parts = [`${summary.passed} passed`, `${summary.failed} failed`];
	if (summary.noTrace) parts.push(`${summary.noTrace} with no trace`);
	return `Checked ${summary.checked} ${summary.checked === 1 ? 'reply' : 'replies'}: ${parts.join(', ')}.${
		summary.capped ? ' Only the newest 500 were checked; narrow the dates to check older ones.' : ''
	}`;
};

/** The reply filter the log and "Run checks" share. */
const toReplyFilter = (filters: Pick<AutomationLogFilters, 'outcome' | 'intent' | 'checks' | 'days'>, q?: string): AutomationReplyFilter => ({
	q: q || undefined,
	outcome: filters.outcome === 'all' ? undefined : filters.outcome,
	intent: filters.intent === ALL_INTENTS ? undefined : filters.intent,
	checks: filters.checks === 'all' ? undefined : filters.checks,
	...sentBetween(filters.days)
});

/** Search, dates, Filters and Run checks for the page header. */
export function AutomationLogsTools({ filters }: { filters: AutomationLogFilters }) {
	const { can } = usePermissions();
	const selectedBrandId = useAtomValue(selectedBusinessUnitIdAtom);
	const [runningChecks, setRunningChecks] = useState(false);
	const activeCount = [filters.outcome !== 'all', filters.intent !== ALL_INTENTS, filters.checks !== 'all'].filter(Boolean).length;

	// Checks the replies the dates, intent and outcome pick: automation that has already gone out.
	async function runChecks() {
		const { from, to, intent, outcome } = toReplyFilter(filters);
		setRunningChecks(true);
		try {
			const summary = await runAutomationChecks({ from, to, intent, outcome }, selectedBrandId ? { bu_id: selectedBrandId } : {});
			toast.success(runSummaryText(summary));
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to run the automation checks'));
		} finally {
			setRunningChecks(false);
		}
	}

	return (
		<>
			<SearchInput
				value={filters.query}
				onChange={(e) => filters.setQuery(e.target.value)}
				placeholder='Search ticket ID or reply text'
				aria-label='Search automation logs'
				className='h-8 w-[240px] border-gray-500 bg-white'
			/>
			<SentDateRange value={filters.days} onChange={filters.setDays} className='w-[240px]' />
			<FiltersPopover activeCount={activeCount}>
				<FilterField label='Status'>
					<SettingsSelect
						value={filters.outcome}
						onValueChange={(value) => filters.setOutcome(value as AutomationOutcomeFilter)}
						options={OUTCOME_OPTIONS}
						aria-label='Status'
					/>
				</FilterField>
				<FilterField label='Intent'>
					<SettingsSelect
						value={filters.intent}
						onValueChange={filters.setIntent}
						options={[
							{ value: ALL_INTENTS, label: 'All intents' },
							...filters.intents.map((name) => ({ value: name, label: <span className='capitalize'>{humanize(name)}</span> }))
						]}
						aria-label='Intent'
					/>
				</FilterField>
				<FilterField label='Checks'>
					<SettingsSelect
						value={filters.checks}
						onValueChange={(value) => filters.setChecks(value as AutomationChecksFilter)}
						options={CHECKS_OPTIONS}
						aria-label='Automation checks'
					/>
				</FilterField>
			</FiltersPopover>
			{can('edit', 'automation') && (
				<button
					type='button'
					disabled={runningChecks}
					onClick={() => void runChecks()}
					title="Check the replies these dates, intent and outcome pick against their intents' automation checks"
					className={SETTINGS_HEADER_BUTTON_CLASS}
				>
					<ListChecks className={cn('size-3.5', runningChecks && 'animate-pulse')} />
					{runningChecks ? 'Checking…' : 'Run checks'}
				</button>
			)}
		</>
	);
}

/** Where a gap opens on the gap board. */
const gapPath = (gap: AutomationReplyGap): string => `/gap-report/${encodeURIComponent(gap.id)}`;

/** The id the gap board shows, so the two can be matched by eye. */
const gapLabel = (gap: AutomationReplyGap): string => formatDisplayId(gap.displayId, gap.id);

/**
 * A link to the gap from inside a clickable row: the click and its Enter key stay on the link, so
 * they open the gap and not the row's sheet.
 */
function GapLink({
	gap,
	children,
	className,
	withIcon = true
}: {
	gap: AutomationReplyGap;
	children: ReactNode;
	className?: string;
	/** The table cell drops the arrow: a seven-digit id already fills the column. */
	withIcon?: boolean;
}) {
	return (
		<Link
			to={gapPath(gap)}
			onClick={(e) => e.stopPropagation()}
			onKeyDown={(e) => e.stopPropagation()}
			className={cn('inline-flex min-w-0 items-center gap-0.5 font-medium text-indigo-500 hover:underline', className)}
		>
			<span className='truncate'>{children}</span>
			{withIcon && <ArrowUpRight className='size-3 shrink-0' aria-hidden='true' />}
		</Link>
	);
}

function GapCell({ gap }: { gap: AutomationReplyGap | null | undefined }) {
	if (!gap) {
		return (
			<span className='text-gray-700' title='Not on a gap'>
				—
			</span>
		);
	}
	return (
		<span className='min-w-0' title={`${gap.title} · ${formatStatusLabel(toGapStatus(gap.status), gap.status)}`}>
			<GapLink gap={gap} withIcon={false} className='max-w-full text-[12px] tabular-nums'>
				{gapLabel(gap)}
			</GapLink>
		</span>
	);
}

const AUTOMATION_LOG_CELLS: Record<string, (reply: AutomationReply) => ReactNode> = {
	time: (reply) => <span className='truncate tabular-nums'>{format(new Date(reply.sentAt), 'd MMM yyyy, HH:mm')}</span>,
	helpdesk: (reply) => <span className='truncate'>{providerOf(reply.provider).label}</span>,
	channel: (reply) => {
		if (!reply.channel) return <span className='text-gray-700'>—</span>;
		const Icon = CHANNEL_ICONS[reply.channel] ?? Radio;
		return (
			<span className='flex min-w-0 items-center gap-2 capitalize'>
				<Icon className='size-4 shrink-0 text-gray-900' />
				<span className='truncate'>{reply.channel}</span>
			</span>
		);
	},
	ticket: (reply) => <span className='truncate'>#{reply.ticketId}</span>,
	review: (reply) => {
		if (reply.outcome === 'blocked') return <span className='text-gray-700'>—</span>;
		if (!reply.review) return <StatusTag tone='grey'>Not reviewed</StatusTag>;
		const verdict = reply.review.verdict === 'correct' ? 'Correct' : 'Incorrect';
		return (
			<span title={`${verdict}${reply.review.reviewerName ? ` · ${reply.review.reviewerName}` : ''}`}>
				<StatusTag tone='blue'>Reviewed</StatusTag>
			</span>
		);
	},
	intent: (reply) => {
		const [first, ...rest] = reply.intents;
		if (!first) return <span className='text-gray-700'>—</span>;
		return (
			<span className='flex min-w-0 items-center gap-1'>
				<span className='truncate capitalize'>{humanize(first)}</span>
				{rest.length > 0 && <span className='shrink-0 text-[11px] text-gray-800'>+{rest.length}</span>}
			</span>
		);
	},
	message: (reply) => (
		<span className={reply.message ? 'line-clamp-2' : 'text-gray-700'}>{reply.message || 'No draft was kept.'}</span>
	),
	status: (reply) => (
		<span className='flex flex-col items-start gap-1'>
			{reply.outcome === 'blocked' ? (
				<span title={reply.block ? `${blockedByLabel(reply.block.by)}: ${blockReasonLabel(reply.block.reason)}` : undefined}>
					<StatusTag tone='amber'>Not sent</StatusTag>
				</span>
			) : (
				<StatusTag tone='green'>Sent</StatusTag>
			)}
			<ChecksBadge checks={reply.checks} />
		</span>
	),
	confidence: (reply) => <span className='tabular-nums'>{reply.confidence != null ? reply.confidence.toFixed(2) : '—'}</span>,
	gap: (reply) => <GapCell gap={reply.gap} />
};

const automationLogTable = (canViewGaps: boolean): SettingsColumn<AutomationReply>[] =>
	automationLogColumns(canViewGaps).map((head) => ({ ...head, cell: AUTOMATION_LOG_CELLS[head.id] }));

function MetaRow({ label, value, copy }: { label: string; value: React.ReactNode; copy?: string | null }) {
	return (
		<div className='grid grid-cols-[110px_1fr] items-baseline gap-3 py-2 border-b border-gray-100 last:border-0'>
			<dt className='text-[12px] text-gray-800'>{label}</dt>
			<dd className='flex items-center gap-2 min-w-0 text-[13px] text-ink'>
				<span className='truncate'>{value}</span>
				{copy && <CopyButton text={copy} label={label} />}
			</dd>
		</div>
	);
}

/** The gap the held turn's hold-time investigation landed on, or that it is on none. */
function GapBand({ gap }: { gap: AutomationReplyGap | null }) {
	return (
		<section aria-label='Gap' className='flex items-center gap-3 rounded-[14px] border border-gray-300 px-4 py-3'>
			<Target className='size-4 shrink-0 text-gray-800' aria-hidden='true' />
			{gap ? (
				<>
					<div className='min-w-0 flex-1'>
						<p className='text-[12px] text-gray-800'>Investigated into</p>
						<p className='truncate text-[13px] font-medium text-ink' title={gap.title}>
							Gap {gapLabel(gap)} · {gap.title}
						</p>
					</div>
					<StatusBadge status={toGapStatus(gap.status)} rawStatus={gap.status} className='shrink-0' />
					<GapLink gap={gap} className='shrink-0 text-[12px]'>
						Open gap
					</GapLink>
				</>
			) : (
				<p className='text-[12px] text-gray-900'>Not on a gap.</p>
			)}
		</section>
	);
}

function ReplyDetails({
	reply,
	brandName,
	canCheck,
	canViewGaps
}: {
	reply: AutomationReply;
	brandName: string | undefined;
	canCheck: boolean;
	canViewGaps: boolean;
}) {
	const sent = new Date(reply.sentAt);
	return (
		<div className='flex flex-col gap-5 px-5 pb-6'>
			{reply.block && (
				<section aria-label='Why it was not sent' className='flex flex-col gap-1 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3'>
					<p className='text-[12px] font-medium text-red-700'>Not sent · stopped by {blockedByLabel(reply.block.by)}</p>
					<p className='text-[13px] leading-[18px] text-ink'>{blockReasonLabel(reply.block.reason)}</p>
					<p className='font-mono text-[11px] text-gray-800'>{reply.block.reason}</p>
				</section>
			)}
			{canViewGaps && reply.outcome === 'blocked' && reply.gap !== undefined && <GapBand gap={reply.gap} />}
			<div className='relative rounded-[14px] border border-gray-300 bg-gray-50 px-4 py-3'>
				{reply.message ? (
					<>
						<div className='absolute right-3 top-3'>
							<CopyButton text={reply.message} label='message' />
						</div>
						<p className='whitespace-pre-wrap pr-6 text-[13px] leading-[20px] text-ink'>{reply.message}</p>
					</>
				) : (
					<p className='text-[13px] leading-[20px] text-gray-900'>No draft was kept.</p>
				)}
			</div>
			<dl>
				<MetaRow label='Ticket' value={<span className='font-mono'>#{reply.ticketId}</span>} copy={reply.ticketId} />
				<MetaRow label='Helpdesk' value={<ProviderBadge provider={reply.provider} />} />
				<MetaRow label='Channel' value={<span className='capitalize'>{reply.channel ?? '—'}</span>} />
				<MetaRow label='Brand' value={brandName ?? (reply.buId != null ? `Brand ${reply.buId}` : 'Account')} />
				<MetaRow
					label='Incoming intents'
					value={reply.intents.length ? <span className='capitalize'>{reply.intents.map(humanize).join(', ')}</span> : '—'}
				/>
				<MetaRow
					label='Final intents'
					value={reply.finalIntents?.length ? <span className='capitalize'>{reply.finalIntents.map(humanize).join(', ')}</span> : '—'}
				/>
				<MetaRow label='Confidence' value={reply.confidence != null ? `${Math.round(reply.confidence * 100)}%` : '—'} />
				<MetaRow label={reply.outcome === 'blocked' ? 'Held back' : 'Sent'} value={format(sent, 'd MMM yyyy, HH:mm:ss')} />
				<MetaRow label='Request ID' value={<span className='font-mono text-[12px]'>{reply.requestId ?? '—'}</span>} copy={reply.requestId} />
				<MetaRow label='Trace ID' value={<span className='font-mono text-[12px]'>{reply.traceId ?? '—'}</span>} copy={reply.traceId} />
			</dl>
			<ReplyChecks reply={reply} canCheck={canCheck} />
			{reply.outcome !== 'blocked' && (
			<section className='flex flex-col gap-2' aria-label='Sentinel review'>
				<p className='text-[12px] font-medium text-ink'>Sentinel review</p>
				{reply.review ? (
					<div className='flex flex-col gap-2 rounded-[14px] border border-gray-300 px-4 py-3'>
						<div className='flex items-center gap-2'>
							<ReviewBadge review={reply.review} />
							<span className='text-[12px] text-gray-900'>
								{reply.review.reviewerName ?? 'A Sentinel'} · {format(new Date(reply.review.reviewedAt), 'd MMM yyyy, HH:mm')}
							</span>
						</div>
						{reply.review.reasons.length > 0 && (
							<ul className='flex flex-wrap gap-1.5'>
								{reply.review.reasons.map((reason) => (
									<li key={reason} className='rounded-full border border-gray-300 px-2 py-0.5 text-[11px] text-gray-900'>
										{reason}
									</li>
								))}
							</ul>
						)}
						{reply.review.note && <p className='whitespace-pre-wrap text-[13px] leading-[18px] text-ink'>{reply.review.note}</p>}
					</div>
				) : (
					<p className='text-[12px] text-gray-900'>Not reviewed yet. It waits in the Sentinel Workspace's Review queue.</p>
				)}
			</section>
			)}
		</div>
	);
}

export function SentRepliesTab({ filters }: { filters: AutomationLogFilters }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const [q, setQ] = useState('');
	const { can } = usePermissions();
	const canViewGaps = can('view', 'gap-report');
	const columns = useMemo(() => automationLogTable(canViewGaps), [canViewGaps]);
	const { outcome, intent, checks, days } = filters;
	// Memoised on the values: the replies hook makes a new query whenever this object changes.
	const filter = useMemo(() => toReplyFilter({ outcome, intent, checks, days }, q), [outcome, intent, checks, days, q]);
	const filtered = Boolean(filter.q || filter.from || filter.outcome || filter.intent || filter.checks);
	const { entries, isLoading, isError, refetch, hasMore, loadingMore, loadMore } = useAutomationReplies(filter);

	const { businessUnits } = useBusinessUnits();
	const brandNameOf = (buId: number | null) => businessUnits.find((unit) => unit.id === String(buId))?.name;

	// Search waits for a pause in typing, so each key does not refetch.
	useEffect(() => {
		const timer = setTimeout(() => setQ(filters.query.trim()), 300);
		return () => clearTimeout(timer);
	}, [filters.query]);

	// Opened from a row, or from the Slack alert's link (`entry`, and the `account` it belongs to).
	// The link is taken into state and cleared from the URL, so every new link opens its entry and
	// leaving the tab does not reopen an old one.
	const linkedId = searchParams.get('entry');
	const [openId, setOpenId] = useState<string | null>(linkedId);
	const [linkedAccount, setLinkedAccount] = useState<string | null>(searchParams.get('account'));
	useEffect(() => {
		if (!linkedId) return;
		setOpenId(linkedId);
		setLinkedAccount(searchParams.get('account'));
		const next = new URLSearchParams(searchParams);
		next.delete('entry');
		next.delete('account');
		setSearchParams(next, { replace: true });
	}, [linkedId]); // eslint-disable-line react-hooks/exhaustive-deps -- runs once per new link
	const currentUser = useAtomValue(currentUserAtom);
	const linked = useAutomationReply(openId && !entries.some((e) => e.id === openId) ? openId : null);
	const open = entries.find((entry) => entry.id === openId) ?? (openId ? linked.data : undefined);
	const otherAccount = linkedAccount && currentUser && linkedAccount !== String(currentUser.accountId) ? linkedAccount : null;
	const close = () => {
		setOpenId(null);
		setLinkedAccount(null);
	};

	return (
		<>
			{isError ? (
				<div className='flex flex-col items-center gap-3 py-12'>
					<p className='text-[13px] text-gray-900'>Automation logs could not be loaded.</p>
					<Button variant='outline' size='sm' onClick={() => void refetch()}>
						Retry
					</Button>
				</div>
			) : (
				<SettingsTableRows
					columns={columns}
					rows={entries}
					rowKey={(reply) => reply.id}
					isLoading={isLoading}
					onRowClick={(reply) => setOpenId(reply.id)}
					empty={
						<div className='flex flex-col items-center gap-3'>
							<span className='flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50'>
								<Send className='h-4 w-4 text-indigo-600' />
							</span>
							<p className='text-[13px] font-medium text-ink'>{filtered ? 'Nothing matches' : 'No automation yet'}</p>
							<p className='max-w-[420px] text-[12px] leading-4 text-gray-900'>
								{filtered
									? 'Try another filter or intent, other dates, or another ticket ID or word.'
									: 'When Kim answers a ticket on its own for this brand, or holds a reply back, it appears here with the message and why. Kept for six months.'}
							</p>
						</div>
					}
				/>
			)}
			{hasMore && (
				<div className='flex justify-center py-4'>
					<Button variant='outline' size='sm' onClick={loadMore} disabled={loadingMore} className='rounded-full'>
						{loadingMore ? 'Loading…' : 'Load older entries'}
					</Button>
				</div>
			)}

			<Sheet open={openId != null} onOpenChange={(isOpen) => !isOpen && close()}>
				<SheetContent className='w-[520px] sm:max-w-[520px] overflow-y-auto'>
					<SheetHeader className='px-5 pt-5'>
						<SheetTitle className='text-[15px]'>{open ? `Reply to ticket #${open.ticketId}` : 'Reply'}</SheetTitle>
						<SheetDescription className='text-[12px]'>
							{open
								? `${open.outcome === 'blocked' ? 'Drafted, not sent' : 'Sent by Kim on its own'} · ${format(new Date(open.sentAt), 'd MMM yyyy, HH:mm')}`
								: linked.isFetching
									? 'Loading…'
									: otherAccount
										? `This reply belongs to account ${otherAccount}. Open Kim AI as that account to see it.`
										: 'This reply could not be found. It may be older than six months.'}
						</SheetDescription>
					</SheetHeader>
					{open ? <ReplyDetails reply={open} brandName={brandNameOf(open.buId)} canCheck={can('edit', 'automation')} canViewGaps={canViewGaps} /> : linked.isFetching && <Skeleton className='mx-5 h-40' />}
				</SheetContent>
			</Sheet>
		</>
	);
}
